import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  User, Award, BookOpen, CheckCircle, Users, Building, Settings, Bell, Menu, X, Shield, Target, 
  Lightbulb, Calendar, BarChart3, LogOut, Home, FileText, Globe, Star, TrendingUp, UserCheck,
  Search, Filter, Download, Share2, Edit, Plus, Eye, Clock, AlertCircle, CheckCircle2, ChevronRight,
  ShoppingBag
} from 'lucide-react';

// Import B2B components
import UserManagement from '../components/B2B/UserManagement';
import MentorPartnerProgram from '../components/B2B/MentorPartnerProgram';
// SystemAdminDashboard is loaded via direct route in App.js
import Marketplace from './Marketplace';

const PlatformApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing authentication
    const authStatus = localStorage.getItem('cness_authenticated');
    const userRole = localStorage.getItem('cness_user_role');
    const username = localStorage.getItem('cness_username');
    
    console.log('PlatformApp initialization - Auth status:', authStatus, 'Role:', userRole, 'Username:', username);
    
    // CRITICAL FIX: Redirect special roles IMMEDIATELY before rendering anything
    if (authStatus === 'true' && userRole) {
      if (userRole === 'super_admin' || userRole === 'system_admin') {
        console.log('Super admin detected - redirecting to /system-admin');
        window.location.replace('/system-admin');
        return; // Stop execution - don't render anything
      }
      if (userRole === 'mentor') {
        window.location.replace('/mentor/dashboard');
        return;
      }
      if (userRole === 'partner') {
        window.location.replace('/partner/dashboard');
        return;
      }
      if (userRole === 'evaluator') {
        window.location.replace('/evaluator/dashboard');
        return;
      }
    }
    
    if (authStatus === 'true' && userRole && username) {
      setIsAuthenticated(true);
      console.log('User authenticated with role:', userRole);
    } else {
      // Clear incomplete authentication and redirect to login
      localStorage.removeItem('cness_authenticated');
      localStorage.removeItem('cness_user_role');
      localStorage.removeItem('cness_username');
      setIsAuthenticated(false);
      console.log('Cleared incomplete authentication - showing login');
    }
    setLoading(false);
  }, []);

  const handleLogin = async (username, password) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Call backend auth API
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const error = await response.json();
        return false;
      }

      const user = await response.json();
      
      // Store user data in localStorage INCLUDING organization_id
      localStorage.setItem('cness_authenticated', 'true');
      localStorage.setItem('cness_user_role', user.role);
      localStorage.setItem('cness_username', user.username);
      localStorage.setItem('cness_user_id', user.id);
      
      // CRITICAL: Store organization_id ONLY in sessionStorage to prevent cross-tab contamination
      if (user.organization_id) {
        sessionStorage.setItem('cness_organization_id', user.organization_id);
        console.log('✅ Stored org ID in sessionStorage (tab-specific):', user.organization_id);
      } else {
        console.error('❌ No organization_id in user object:', user);
      }
      
      setIsAuthenticated(true);
      
      // Store userData for dashboard use
      localStorage.setItem('userData', JSON.stringify(user));
      
      // Redirect based on role
      if (user.role === 'system_admin' || user.role === 'super_admin') {
        navigate('/system-admin');
      } else if (user.role === 'org_admin') {
        navigate('/platform');
      } else if (user.role === 'evaluator') {
        navigate('/evaluator/dashboard');
      } else if (user.role === 'mentor') {
        navigate('/mentor/dashboard');
      } else if (user.role === 'partner') {
        navigate('/partner/dashboard');
      }
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const handleLogout = () => {
    // Clear all authentication data from both localStorage and sessionStorage
    localStorage.removeItem('cness_authenticated');
    localStorage.removeItem('cness_user_role');
    localStorage.removeItem('cness_username');
    localStorage.removeItem('cness_user_id');
    localStorage.removeItem('userData');
    sessionStorage.removeItem('cness_organization_id');
    sessionStorage.removeItem('cness_assessment_session');
    sessionStorage.removeItem('cness_payment_complete');
    console.log('[PlatformApp] Logout: Cleared all session data');
    
    // Update authentication state
    setIsAuthenticated(false);
    
    // Redirect to main landing page
    console.log('User logged out - redirecting to main landing page');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading CNESS Platform...</h2>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <B2BLogin onLogin={handleLogin} />;
  }

  return <RoleBasedDashboard onLogout={handleLogout} />;
};

// B2B Login Component
const B2BLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    setTimeout(() => {
      if (onLogin(credentials.username, credentials.password)) {
        // Login successful
      } else {
        setError('Invalid credentials');
      }
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-gray-900">
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/mkt20roc_cnesslogo_neqkfd.webp" 
            alt="CNESS — Consciousness Triumphs" 
            className="h-12 w-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800">CNESS B2B Platform</h1>
          <p className="text-gray-600 text-sm">Conscious Enterprise Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              placeholder="Enter your username"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              placeholder="Enter your password"
              required
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Authenticating...
              </>
            ) : (
              'Access Platform'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Role-Based Dashboard Component
const RoleBasedDashboard = ({ onLogout }) => {
  const [currentRole, setCurrentRole] = useState('');
  const [username, setUsername] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeModule, setActiveModule] = useState(searchParams.get('module') || 'dashboard');
  const initialCourseId = searchParams.get('course') || null;
  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem('cness_user_role');
    const user = localStorage.getItem('cness_username');
    
    // Debug logging
    console.log('Retrieved role from localStorage:', role);
    console.log('Retrieved username from localStorage:', user);
    
    // CRITICAL FIX: Redirect super_admin to /system-admin immediately
    if (role === 'super_admin' || role === 'system_admin') {
      console.log('Super admin detected - redirecting to /system-admin');
      window.location.href = '/system-admin';
      return;
    }
    
    // Redirect mentor and partner to their dashboards
    if (role === 'mentor') {
      window.location.href = '/mentor/dashboard';
      return;
    }
    
    if (role === 'partner') {
      window.location.href = '/partner/dashboard';
      return;
    }
    
    if (role === 'evaluator') {
      window.location.href = '/evaluator/dashboard';
      return;
    }
    
    if (role && user) {
      setCurrentRole(role);
      setUsername(user);
    } else {
      // If no role found, something went wrong - set defaults but log the issue
      console.warn('No role or username found in localStorage - authentication may have failed');
      setCurrentRole('explorer');
      setUsername('user');
    }
  }, [navigate]);

  // Role configurations as per FRD
  const roleConfig = {
    explorer: {
      name: 'Explorer',
      badge: 'Discovering CNESS',
      color: 'bg-gray-500',
      modules: ['dashboard', 'pillars', 'directory', 'best-practices'],
      canEdit: false,
      description: 'Explore the CNESS ecosystem and learn about conscious business practices'
    },
    org_admin: {
      name: 'Organization Admin',
      badge: 'Certified Leader',
      color: 'bg-blue-500', 
      modules: ['dashboard', 'true-profile', 'pillars', 'assessment', 'best-practices', 'directory', 'mentor-partner', 'users'],
      canEdit: true,
      description: 'Manage your organization journey through CNESS certification'
    },
    mentor: {
      name: 'Mentor',
      badge: 'CNESS Mentor',
      color: 'bg-green-500',
      modules: ['dashboard', 'mentor-tools', 'organizations', 'resources', 'directory'],
      canEdit: false,
      description: 'Guide organizations on their conscious business transformation'
    },
    partner: {
      name: 'Partner',
      badge: 'CNESS Partner',
      color: 'bg-purple-500',
      modules: ['dashboard', 'partner-tools', 'opportunities', 'directory', 'resources'],
      canEdit: false,
      description: 'Connect with certified organizations and provide services'
    },
    evaluator: {
      name: 'Evaluator',
      badge: 'CNESS Evaluator', 
      color: 'bg-orange-500',
      modules: ['dashboard', 'evaluation-queue', 'scoring', 'organizations', 'reports'],
      canEdit: true,
      description: 'Evaluate and score organization assessments and profiles'
    },
    super_admin: {
      name: 'Super Admin',
      badge: 'System Administrator',
      color: 'bg-red-500',
      modules: ['dashboard', 'admin-panel', 'pillars-cms', 'users', 'organizations', 'system', 'analytics', 'reports'],
      canEdit: true,
      description: 'Complete system administration and oversight'
    }
  };

  // Get current role config
  const currentConfig = roleConfig[currentRole] || roleConfig.explorer;

  // Navigation items based on role
  const getNavItems = () => {
    const baseItems = [
      { id: 'dashboard', label: 'Dashboard', icon: Home, active: activeModule === 'dashboard' }
    ];

    const moduleItems = {
      'true-profile': { label: 'True Profile', icon: User, description: 'Organization Profile Setup' },
      'pillars': { label: 'Learning Hub', icon: BookOpen, description: 'Six-pillar learning journeys' },
      'assessment': { label: 'Assessment Engine', icon: Target, description: 'Certification Assessment' },
      'best-practices': { label: 'Best Practices Hub', icon: Lightbulb, description: 'Share & Discover' },
      'directory': { label: 'Recognition Directory', icon: Building, description: 'Certified Organizations' },
      'marketplace': { label: 'Marketplace', icon: ShoppingBag, description: 'Purpose Commerce Hub' },
      'mentor-partner': { label: 'Mentor & Partner Program', icon: Users, description: 'Connect & Collaborate' },
      'users': { label: 'User Management', icon: UserCheck, description: 'Manage Team Members' },
      'mentor-tools': { label: 'Mentor Tools', icon: Star, description: 'Mentoring Resources' },
      'organizations': { label: 'Organizations', icon: Building, description: 'Manage Organizations' },
      'partner-tools': { label: 'Partner Tools', icon: Globe, description: 'Partnership Tools' },
      'opportunities': { label: 'Opportunities', icon: TrendingUp, description: 'Business Opportunities' },
      'evaluation-queue': { label: 'Evaluation Queue', icon: Clock, description: 'Pending Evaluations' },
      'scoring': { label: 'Assessment Scoring', icon: CheckCircle2, description: 'Score Assessments' },
      'admin-panel': { label: 'Admin Panel', icon: Shield, description: 'System Administration' },
      'pillars-cms': { label: '6 Pillars CMS', icon: BookOpen, description: 'Manage Learning Content' },
      'system': { label: 'System Config', icon: Settings, description: 'System Settings' },
      'analytics': { label: 'Analytics', icon: BarChart3, description: 'Platform Analytics' },
      'reports': { label: 'Reports', icon: FileText, description: 'Generate Reports' },
      'resources': { label: 'Resources', icon: BookOpen, description: 'Learning Resources' }
    };

    // Add role-specific modules
    currentConfig.modules.forEach(moduleId => {
      if (moduleId !== 'dashboard' && moduleItems[moduleId]) {
        baseItems.push({
          id: moduleId,
          label: moduleItems[moduleId].label,
          icon: moduleItems[moduleId].icon,
          active: activeModule === moduleId,
          description: moduleItems[moduleId].description
        });
      }
    });

    return baseItems;
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Collapsible Sidebar */}
      <div className={`sticky-sidebar ${sidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg transition-all duration-300 flex flex-col border-r border-gray-200`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className={`${sidebarOpen ? 'block' : 'hidden'}`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/mkt20roc_cnesslogo_neqkfd.webp" 
                alt="CNESS — Consciousness Triumphs" 
                className="h-10 w-auto"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* User Profile Section */}
        <div className="p-4 border-b border-gray-200">
          <div className={`flex items-center ${sidebarOpen ? 'space-x-3' : 'justify-center'}`}>
            <div className={`w-10 h-10 rounded-full ${currentConfig.color} flex items-center justify-center text-white text-sm font-bold`}>
              {username.charAt(0).toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{username}</p>
                <p className="text-xs text-gray-500 truncate">{currentConfig.name}</p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <div className={`mt-2 px-2 py-1 rounded-full text-xs font-medium text-white ${currentConfig.color}`}>
              {currentConfig.badge}
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveModule(item.id)}
                className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-0'} py-2 rounded-lg transition-colors ${
                  item.active 
                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title={sidebarOpen ? '' : item.label}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && (
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium truncate">{item.label}</p>
                    {item.description && (
                      <p className="text-xs text-gray-500 truncate">{item.description}</p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onLogout}
            className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-0'} py-2 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors`}
            title={sidebarOpen ? '' : 'Logout'}
            data-testid="logout-button-sidebar"
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Header */}
        <header className="sticky-header bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 capitalize">
                {activeModule === 'dashboard'
                  ? `${currentConfig.name} Dashboard`
                  : (navItems.find((i) => i.id === activeModule)?.label || activeModule.replace('-', ' '))}
              </h1>
              <p className="text-gray-600 text-sm">
                {activeModule === 'dashboard'
                  ? currentConfig.description
                  : (navItems.find((i) => i.id === activeModule)?.description || currentConfig.description)}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 rounded-lg hover:bg-gray-100 relative">
                <Bell className="h-5 w-5 text-gray-600" />
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
              </button>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${currentConfig.color}`}>
                  {currentConfig.badge}
                </span>
              </div>
              {/* Logout Button - Right Top */}
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center space-x-2 shadow-md hover:shadow-lg"
                data-testid="logout-button-top"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Dynamic Content Based on Active Module */}
        <main className="flex-1 p-6 overflow-visible">
          <ModuleContent role={currentRole} activeModule={activeModule} setActiveModule={setActiveModule} initialCourseId={initialCourseId} />
        </main>
      </div>
    </div>
  );
};

// Import all B2B components
import OrgAdminDashboard from '../components/B2B/OrgAdminDashboard';
import TrueProfile from '../components/B2B/TrueProfile';
import PillarsLearning from '../components/B2B/PillarsLearning';
import LearningHubRouter from '../components/learning/LearningHubRouter';
import AssessmentEngine from '../components/B2B/AssessmentEngine';
import BestPracticesHub from '../components/B2B/BestPracticesHub';
import Directory from '../components/B2B/Directory';

// Module Content Component
const ModuleContent = ({ role, activeModule, setActiveModule, initialCourseId = null }) => {
  console.log('ModuleContent rendering with role:', role, 'activeModule:', activeModule);
  
  // Organization Admin specific modules
  if (role === 'org_admin') {
    console.log('Rendering Organization Admin content for module:', activeModule);
    switch (activeModule) {
      case 'dashboard':
        return <OrgAdminDashboard setActiveModule={setActiveModule} />;
      case 'true-profile':
        return <TrueProfile />;
      case 'pillars':
        return <LearningHubRouter initialCourseId={initialCourseId} />;
      case 'assessment':
        return <AssessmentEngine setActiveModule={setActiveModule} />;
      case 'best-practices':
        return <BestPracticesHub />;
      case 'directory':
        return <Directory />;
      case 'marketplace':
        return <Marketplace />;
      case 'users':
        return <UserManagement />;
      case 'mentor-partner':
        return <MentorPartnerProgram />;
      default:
        return <DashboardContent role={role} setActiveModule={setActiveModule} />;
    }
  }
  
  // Default dashboard for other roles
  if (activeModule === 'dashboard') {
    return <DashboardContent role={role} setActiveModule={setActiveModule} />;
  }
  
  // Return specific module content based on activeModule and role
  switch (activeModule) {
    case 'pillars-cms':
      // Redirect to dedicated system-admin route
      window.location.href = '/system-admin';
      return null;
    case 'pillars':
      return <LearningHubRouter initialCourseId={initialCourseId} />;
    case 'directory':
      return <Directory />;
    case 'marketplace':
      return <Marketplace />;
    case 'best-practices':
      return <BestPracticesHub />;
    default:
      return (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 capitalize">
              {activeModule.replace('-', ' ')} Module
            </h3>
            <p className="text-gray-600">
              This module is under development. Content will be available soon.
            </p>
          </div>
        </div>
      );
  }
};

// Import full components - already imported at top

// Dashboard Content Component
const DashboardContent = ({ role, setActiveModule }) => {
  const [showPillarsCMSModal, setShowPillarsCMSModal] = React.useState(false);
  const [pillarsData, setPillarsData] = React.useState({ totalPillars: 0, totalModules: 0, organizations: 0 });

  React.useEffect(() => {
    if (role === 'super_admin') {
      // Fetch pillars stats
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pillars/admin/all`)
        .then(res => res.json())
        .then(data => {
          const totalModules = data.reduce((sum, p) => sum + (p.sub_pillars?.length || 0), 0);
          setPillarsData({
            totalPillars: data.length,
            totalModules,
            organizations: 156 // This should come from org progress API
          });
        })
        .catch(err => console.error('Error fetching pillars:', err));
    }
  }, [role]);

  const getDashboardData = () => {
    switch (role) {
      case 'explorer':
        return {
          stats: [
            { label: 'Pillars Explored', value: '3/8', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Learning Progress', value: '25%', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Resources Viewed', value: '12', color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Directory Entries', value: '45', color: 'text-orange-600', bg: 'bg-orange-50' }
          ],
          quickActions: [
            { title: 'Continue Learning', desc: 'Resume 6 Pillars course', icon: BookOpen, color: 'bg-blue-600' },
            { title: 'Explore Directory', desc: 'View certified organizations', icon: Building, color: 'bg-green-600' },
            { title: 'Best Practices', desc: 'Discover industry insights', icon: Lightbulb, color: 'bg-purple-600' }
          ]
        };
      
      case 'org_admin':
        return {
          stats: [
            { label: 'Profile Completion', value: '85%', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Assessment Progress', value: '6/8', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Team Members', value: '12', color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Certification Level', value: 'Level 2', color: 'text-orange-600', bg: 'bg-orange-50' }
          ],
          quickActions: [
            { title: 'Complete Assessment', desc: 'Finish remaining pillars', icon: Target, color: 'bg-blue-600' },
            { title: 'Update Profile', desc: 'Review organization details', icon: User, color: 'bg-green-600' },
            { title: 'Manage Team', desc: 'Add team members', icon: Users, color: 'bg-purple-600' },
            { title: 'View Certificate', desc: 'Download current level', icon: Award, color: 'bg-orange-600' }
          ]
        };
        
      case 'super_admin':
        return {
          stats: [
            { label: 'Total Organizations', value: '156', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Pending Evaluations', value: '23', color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Active Mentors', value: '45', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'System Health', value: '99.9%', color: 'text-purple-600', bg: 'bg-purple-50' }
          ],
          quickActions: [
            { title: '6 Pillars CMS', desc: 'Manage learning content', icon: BookOpen, color: 'bg-indigo-600', action: 'pillars-cms-modal' },
            { title: 'Review Evaluations', desc: 'Process pending assessments', icon: CheckCircle, color: 'bg-red-600' },
            { title: 'System Analytics', desc: 'View platform metrics', icon: BarChart3, color: 'bg-blue-600' },
            { title: 'User Management', desc: 'Manage platform users', icon: UserCheck, color: 'bg-green-600' },
            { title: 'Generate Reports', desc: 'Create system reports', icon: FileText, color: 'bg-purple-600' }
          ]
        };
        
      default:
        return {
          stats: [
            { label: 'Active Tasks', value: '8', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Completion Rate', value: '75%', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Resources', value: '24', color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Progress', value: 'On Track', color: 'text-orange-600', bg: 'bg-orange-50' }
          ],
          quickActions: [
            { title: 'View Tasks', desc: 'Check your assignments', icon: CheckCircle, color: 'bg-blue-600' },
            { title: 'Resources', desc: 'Access learning materials', icon: BookOpen, color: 'bg-green-600' },
            { title: 'Directory', desc: 'Browse organizations', icon: Building, color: 'bg-purple-600' }
          ]
        };
    }
  };

  const dashboardData = getDashboardData();

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardData.stats.map((stat, index) => (
          <div key={index} className={`${stat.bg} rounded-lg p-6 border border-gray-200`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
              <BarChart3 className={`h-8 w-8 ${stat.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardData.quickActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <div 
              key={index} 
              onClick={() => {
                if (action.action === 'pillars-cms-modal') {
                  setShowPillarsCMSModal(true);
                }
              }}
              className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start space-x-4">
                <div className={`${action.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{action.title}</h3>
                  <p className="text-gray-600 text-sm">{action.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 6 Pillars CMS Modal */}
      {showPillarsCMSModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-3xl font-bold mb-2">6 Pillars Learning CMS</h3>
                  <p className="text-indigo-100">Content Management System Overview</p>
                </div>
                <button
                  onClick={() => setShowPillarsCMSModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-8">
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between mb-4">
                    <BookOpen className="h-8 w-8 text-blue-500" />
                    <span className="text-3xl font-bold text-blue-600">{pillarsData.totalPillars}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-600 uppercase">Total Pillars</h4>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between mb-4">
                    <FileText className="h-8 w-8 text-green-500" />
                    <span className="text-3xl font-bold text-green-600">{pillarsData.totalModules}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-600 uppercase">Learning Modules</h4>
                </div>

                <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="h-8 w-8 text-purple-500" />
                    <span className="text-3xl font-bold text-purple-600">{pillarsData.organizations}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-600 uppercase">Organizations Learning</h4>
                </div>
              </div>

              {/* Quick Info */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">CMS Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-900">Content Management</h5>
                      <p className="text-sm text-gray-600">Create, edit, and organize pillar content</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-900">Assessment Builder</h5>
                      <p className="text-sm text-gray-600">Create questions and manage quizzes</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-900">Draft/Publish Workflow</h5>
                      <p className="text-sm text-gray-600">Review content before publishing</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-900">Progress Analytics</h5>
                      <p className="text-sm text-gray-600">Track organization learning progress</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowPillarsCMSModal(false);
                    setActiveModule('pillars-cms');
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-3 text-lg font-semibold"
                >
                  <BookOpen className="h-6 w-6" />
                  <span>Open Full CMS</span>
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role-Specific Content */}
      {role === 'super_admin' && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent System Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-900">New organization registration: Tech Solutions Ltd</span>
              <span className="text-xs text-gray-500">2 hours ago</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-900">Assessment completed by Green Innovations Inc</span>
              <span className="text-xs text-gray-500">4 hours ago</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-900">Mentor Sarah Johnson added new best practice</span>
              <span className="text-xs text-gray-500">1 day ago</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformApp;